import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { type MFAIdentityType } from '@/entities/mfa-identity';
import type { EnableTwoFactorIdentityRequestDto } from '../api/types';
import { useTwoFactorEnable } from '../api/queries/useTwoFactorEnable';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/entities/session';
import { isVerificationCodeInvalidError } from '@/shared/utils';

type TwoFactorIdentityDisableArgs = {
  identityType: MFAIdentityType;
  appKey?: string;
  identity?: string;
  onSuccess: () => void;
};

const useTwoFactorIdentityEnableForm = ({
  identityType,
  appKey,
  identity,
  onSuccess,
}: TwoFactorIdentityDisableArgs) => {
  const { session, refreshSession } = useSessionStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const required = t('authPages.validation.requiredField');
  const otpIncorrect = t('authPages.validation.otpIncorrect');

  const otpCodeSchema = z
    .string()
    .regex(/^\d+$/, { message: otpIncorrect })
    .nonempty({ message: required });

  const schema = z.object({
    key: z.string().optional(),
    identity: z.string().optional(),
    code: otpCodeSchema,
  });

  const form = useForm<EnableTwoFactorIdentityRequestDto>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      key: appKey,
      identity: identity,
      code: '',
    },
  });

  const {
    enableTwoFactor,
    isEnableTwoFactorLoading,
    isEnableTwoFactorError,
    enableTwoFactorError,
    resetEnableTwoFactor,
  } = useTwoFactorEnable({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['user-profile-two-factor-settings', session?.userId],
      });
      await refreshSession();
      onSuccess();
    },
    onError: error => {
      if (isVerificationCodeInvalidError(error)) {
        form.setValue('code', '');
      }
      throw error;
    },
  });

  const submitForm = async () => {
    await enableTwoFactor({ identityType, data: form.getValues() });
  };

  return {
    enableForm: form,
    enableTwoFactorSubmit: submitForm,
    isEnableTwoFactorLoading,
    isEnableTwoFactorError,
    enableTwoFactorError,
    resetEnableTwoFactor,
  };
};

export { useTwoFactorIdentityEnableForm };
